import { Visibility, VisibilityOff, LocationOn } from '@mui/icons-material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ModalComp from '../ModalComp';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Icon } from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import { enqueueSnackbar } from 'notistack';


delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});




const CustomTextField = styled(TextField)(({ theme, paddingTop, inputType }) => ({
  '& .MuiOutlinedInput-root': {
    minHeight: '56px',
    paddingTop: paddingTop,
    '& fieldset': {
      borderRadius: '8px',
      borderColor: 'gray', // Default border color
    },
    '&:hover fieldset': {
      borderColor: 'gray', // Border color when hovering
    },
    '&.Mui-focused fieldset': {
      borderColor: 'gray', // Border color when focused
    },
    ...(inputType === 'date' && {
      paddingRight: '10px'
    }),
  },
  '& .MuiInputLabel-root': {
    color: 'gray', // Default label color
    whiteSpace: 'break-spaces',
    position: 'absolute', // Position the label absolutely to manage alignment
    left: '16px', // Align label with the text input padding
    top: '50%', // Vertically center label
    transform: 'translateY(-50%) scale(1)', // Adjust to align with input
    transformOrigin: 'left', // Label scales from the left
    transition: 'all 0.2s ease-in-out', // Smooth transition for focus effects
    lineHeight: '1.5', // Set label line height
    pointerEvents: 'none', // Prevent label from being clickable
    width: 'max-content',
    ...(inputType === 'date' && {
      color: 'gray', // Label color when focused
      top: '-10px', // Move label above input when focused
      transform: 'translateY(0) scale(0.75)', // Shrink and position label above input  
      backgroundColor: 'white', // Set background color for date input
      paddingLeft: '10px',
      paddingRight: '10px',

    }),
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'gray', // Label color when focused
    top: '-10px', // Move label above input when focused
    transform: 'translateY(0) scale(0.75)', // Shrink and position label above input
  },
  '& .MuiInputLabel-shrink': {
    top: '-10px', // Move label above input when input has value
    transform: 'translateY(0) scale(0.75)', // Shrink the label and move it above the input
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px', // Adjust input padding for better alignment
    lineHeight: '1.5', // Ensuring proper vertical alignment
    paddingRight: '0px'
  },
  '& .MuiFormHelperText-root': {
    marginTop: '4px', // Adjust margin for helper text below input
  },
}));

const CustomInput = ({
  value,
  onChange,
  label,
  type,
  name,
  required,
  error,
  disabled,
  endLabel,
  className,
  maxlength,
  min,
  max ,
  allowFutureDates ,
  allowOnlyFutureDates = false, 
  copy = false
}) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(value || '');


  const MapClickHandler = ({ setMarkerPosition }) => {
    // Hook to handle map events
    useMapEvents({
      click: (e) => {
        setMarkerPosition(e.latlng);
        const { lat, lng } = e.latlng;
        setSelectedLocation(`${lat}, ${lng}`);
      },
    });

    return null; // No visual rendering from this component
  };

  const [markerPosition, setMarkerPosition] = useState({
    lat: 12.983222,
    lng: 77.546188,
  });

  const center = {
    lat: 12.983222,
    lng: 77.546188,
  };

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleConfirmLocation = () => {
    onChange({ target: { name, value: selectedLocation } });

    closeModal();
  }

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const today = new Date().toISOString().split('T')[0];

  const calculatePaddingTop = (label) => {
    const lineCount = label.split('\n').length;
    let basePadding;

    // Adjust base padding based on label length in smaller increments
    if (label.length >= 500) {
      basePadding = 70;
    } else if (label.length >= 350) {
      basePadding = 50;
    } else if (label.length >= 200) {
      basePadding = 30;
    } else if (label.length >= 100) {
      basePadding = 20;
    } else {
      basePadding = 10;
    }

    // Additional padding for each line break
    const extraPadding = (lineCount - 1) * 15; // Increase line break padding if needed

    return `${basePadding + extraPadding}px`;
  };

  const paddingTop = calculatePaddingTop(label); // Get dynamic paddingTop value

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <CustomTextField
        className={`w-[100%] ${className}`}
        onCopy={(e) => {
          if (copy) {
              e.preventDefault();
              enqueueSnackbar('Copying Text is disabled');
          }
      }}
      onFocus={(e) => {
        if (type === 'date') e.target.showPicker(); // Open calendar on focus
      }}
        required={required}
        disabled={disabled}
        InputLabelProps={{
          style: {
            color: 'gray',
          },
        }}
        inputProps={{
          max: type === "date" && !allowFutureDates ? today : undefined, // Disable future dates for non-allowed fields
          min: type === "date" && allowOnlyFutureDates ? today : min,
          maxLength :maxlength ? maxlength : undefined,
        }}
        id="outlined-basic"
        value={value !== undefined ? value : type === 'date' ? '' : ''}
        onChange={onChange}
        type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
        label={label}
        variant="outlined"
        name={name}
        paddingTop={paddingTop}
        inputType={type} // Pass the type as inputType for conditional styling
        onClick={type === 'location on map' ? handleOpenModal : undefined} // 🟢 Open modal on input click
        readOnly={type === 'location on map'} 
      />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          right: '16px', // Adjust spacing from the right edge of the field
          transform: 'translateY(-50%)', // Vertically center the button
        }}
      >
        {endLabel}
        {type === "password" && (
          <IconButton
            aria-label="toggle password visibility"
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            edge="end"
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        )}
        {type === 'location on map' && (
          <IconButton onClick={handleOpenModal} edge="end">
            <MyLocationIcon />
          </IconButton>
        )}
      </div>

      <ModalComp
        closeModal={closeModal}
        padding="10px"
        modalIsOpen={isOpen}
        width="90%"
        maxHeight="600px"
      >
        <Box sx={{ padding: '20px' }}>
          <Typography variant="h6" align="center" sx={{ marginBottom: '20px' }}>
            Select Your Location
          </Typography>
          <MapContainer
            center={center}
            zoom={13}
            style={{ height: '400px', width: '100%', borderRadius: '5px' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; <a href=''>GuideStar India</a> "
            />
            <MapClickHandler setMarkerPosition={setMarkerPosition} />
            <Marker position={markerPosition}>
              <Popup>
                {`Latitude: ${markerPosition.lat}, Longitude: ${markerPosition.lng}`}
              </Popup>
            </Marker>
          </MapContainer>
        </Box>
        <Box sx={{ textAlign: 'center', paddingTop: '15px' }}>
          <button
            onClick={handleConfirmLocation}
            className="bg-blue-500 text-white py-2 px-4 rounded"
          >
            Confirm Location
          </button>
        </Box>
      </ModalComp>
    </div>
  );
};

export default CustomInput;
